import type {
  CalendarEvent,
  CalendarEventId,
} from "@common/types/calendar-event";
import type { GroupKey } from "@common/types/todo";
import type { DisplayMode } from "@common/utils/todo-utils";
import TodoGroupUtils, {
  PRESET_GROUP_KEYS,
} from "@common/utils/todo-group-utils";
import moment from "moment";

export type EventListByGroup = { [groupKey: GroupKey]: CalendarEvent[] };

function filterGroupKey(
  key: GroupKey | undefined,
  displayMode: DisplayMode,
): GroupKey | undefined {
  if (!key) return undefined;

  if (key === PRESET_GROUP_KEYS.BACKLOG || key === PRESET_GROUP_KEYS.UPCOMING) {
    return undefined;
  }

  if (displayMode === "default" || displayMode === "zen") {
    if (key !== PRESET_GROUP_KEYS.TODAY) return undefined;
  }

  if (displayMode === "week" && key === PRESET_GROUP_KEYS.SOMEDAY) {
    return undefined;
  }

  return key;
}

/**
 * All group keys an event should appear in, accounting for multi-day spans.
 * Returns an empty array when the event should be hidden entirely.
 */
function resolvedEventGroupKeys(
  event: CalendarEvent,
  displayMode: DisplayMode,
): GroupKey[] {
  const startMs = event.startTime;
  let endMs = event.endTime;

  if (event.isAllDay && event.startDate && event.endDate) {
    // endDate is exclusive (Google/iCal convention), so the last visible day
    // is the day before endDate.
    endMs = moment(event.endDate).subtract(1, "day").startOf("day").valueOf();
    if (endMs < moment(event.startDate).startOf("day").valueOf()) {
      endMs = moment(event.startDate).startOf("day").valueOf();
    }
  }

  const startDay = moment(startMs).startOf("day");
  const endDay = moment(endMs).startOf("day");

  if (startDay.isSame(endDay)) {
    const key = filterGroupKey(
      TodoGroupUtils.groupKeyForDate(startMs, displayMode),
      displayMode,
    );
    return key ? [key] : [];
  }

  const keys: GroupKey[] = [];
  const seen = new Set<GroupKey>();
  const cursor = startDay.clone();
  while (cursor.isSameOrBefore(endDay)) {
    const key = filterGroupKey(
      TodoGroupUtils.groupKeyForDate(cursor, displayMode),
      displayMode,
    );
    if (key && !seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
    cursor.add(1, "day");
  }

  return keys;
}

/**
 * Bin events into groups using calendar/week column rules.
 * Within each group, events are sorted by startTime ascending.
 */
export function eventsByGroup(
  events: CalendarEvent[],
  displayMode: DisplayMode,
): EventListByGroup {
  const groups: EventListByGroup = {};

  for (const event of events) {
    if (event.deleted) continue;
    for (const key of resolvedEventGroupKeys(event, displayMode)) {
      if (!groups[key]) groups[key] = [];
      groups[key].push(event);
    }
  }

  for (const key of Object.keys(groups)) {
    groups[key]?.sort((a, b) => {
      if (a.startTime !== b.startTime) return a.startTime - b.startTime;
      return a.endTime - b.endTime;
    });
  }

  return groups;
}

/**
 * Filter events to a single date (group key).
 * Returns events sorted by startTime.
 */
export function eventsForGroupKey(
  events: CalendarEvent[],
  groupKey: GroupKey,
  displayMode: DisplayMode,
): CalendarEvent[] {
  const result: CalendarEvent[] = [];
  for (const event of events) {
    if (event.deleted) continue;
    const keys = resolvedEventGroupKeys(event, displayMode);
    if (keys.includes(groupKey)) result.push(event);
  }
  result.sort((a, b) => {
    if (a.startTime !== b.startTime) return a.startTime - b.startTime;
    return a.endTime - b.endTime;
  });
  return result;
}

export type EventIdMap = { [id: CalendarEventId]: CalendarEvent };
